import React, { useState } from 'react';
import { Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import ExcelJS from 'exceljs';
import { getRegistroNombre } from './file-utils';

const REGISTRO_LABELS = {
  participante: 'PARTICIPANTE',
  organizacion: 'ORGANIZACIÓN',
  sin_registro: 'SIN REGISTRO',
};

const ExportarDetallesArchivos = ({
  files,
  project,
  month,
  filesWithParticipante,
  filesWithOrganizacion,
  filesSinRegistro
}) => {
  const [exporting, setExporting] = useState(false);

  const getCleanFileName = (name) => {
    return name.replace(/^\d+-/, "");
  };

  const getIdentificador = (file) => {
    if (file.tipoRegistro === 'organizacion' && file.organizacion) {
      return file.organizacion.Nit || file.organizacion.sigla || 'N/A';
    }
    return file.cedula || 'N/A';
  };

  const exportarAExcel = async () => {
    setExporting(true);

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Detalles Archivos');
      const estiloTitulo = {
        font: { bold: true, size: 16, color: { argb: 'FFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '2C3E50' } },
        alignment: { horizontal: 'center', vertical: 'middle' }
      };

      const estiloSubtitulo = {
        font: { bold: true, size: 12, color: { argb: '000000' } }, // Negro
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E3F2FD' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      const estiloCabecera = {
        font: { bold: true, color: { argb: '000000' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DDEBF7' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      const estiloContenidoTabla = {
        font: { color: { argb: '000000' } },
        alignment: { vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      const estiloContenidoCentrado = {
        ...estiloContenidoTabla,
        alignment: { horizontal: 'center', vertical: 'middle' }
      };

      const estiloEtiquetaEstadistica = {
        font: { bold: true, color: { argb: '000000' } },
        alignment: { horizontal: 'left', vertical: 'middle' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F5F5F5' } },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      const estiloValorEstadistica = {
        font: { color: { argb: '000000' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      worksheet.mergeCells('A1:F1');
      const titulo = worksheet.getCell('A1');
      titulo.value = `DETALLES DE ARCHIVOS - ${project} - ${month}`;
      titulo.style = estiloTitulo;
      const fecha = new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      worksheet.mergeCells('A2:F2');
      worksheet.getCell('A2').value = `Generado el: ${fecha}`;
      worksheet.getCell('A2').style = {
        font: { italic: true, color: { argb: '7F8C8D' } },
        alignment: { horizontal: 'left' }
      };

      worksheet.addRow([]);
      worksheet.mergeCells('A4:F4');
      worksheet.getCell('A4').value = 'RESUMEN DEL PROYECTO';
      worksheet.getCell('A4').style = estiloSubtitulo;

      const porcentajeConRegistro = files.length > 0
        ? Math.round(((filesWithParticipante + filesWithOrganizacion) / files.length) * 100)
        : 0;

      const statsRow1 = worksheet.addRow([
        'Total de archivos:',
        files.length,
        'Con participante:',
        filesWithParticipante,
        '',
        ''
      ]);

      statsRow1.eachCell((cell, colNumber) => {
        if (colNumber === 1 || colNumber === 3) {
          cell.style = estiloEtiquetaEstadistica;
        } else if (colNumber === 2 || colNumber === 4) {
          cell.style = estiloValorEstadistica;
        }
      });

      const statsRow2 = worksheet.addRow([
        'Con organización:',
        filesWithOrganizacion,
        'Sin registro:',
        filesSinRegistro,
        '',
        ''
      ]);

      statsRow2.eachCell((cell, colNumber) => {
        if (colNumber === 1 || colNumber === 3) {
          cell.style = estiloEtiquetaEstadistica;
        } else if (colNumber === 2 || colNumber === 4) {
          cell.style = estiloValorEstadistica;
        }
      });

      const statsRow3 = worksheet.addRow([
        'Proyecto:',
        project,
        'Mes:',
        month,
        'Tasa con registro:',
        `${porcentajeConRegistro}%`
      ]);

      statsRow3.eachCell((cell, colNumber) => {
        if (colNumber === 1 || colNumber === 3 || colNumber === 5) {
          cell.style = estiloEtiquetaEstadistica;
        } else if (colNumber === 2 || colNumber === 4 || colNumber === 6) {
          cell.style = estiloValorEstadistica;
          if (colNumber === 6) {
            if (porcentajeConRegistro >= 90) {
              cell.font = { color: { argb: '2E7D32' } };
            } else if (porcentajeConRegistro >= 70) {
              cell.font = { color: { argb: 'FF9800' } };
            } else {
              cell.font = { color: { argb: 'D32F2F' } };
            }
          }
        }
      });

      worksheet.addRow([]);
      worksheet.addRow([]);
      const cabecera = ['#', 'Archivo', 'Tipo', 'Registrado a', 'Cédula / NIT / Sigla', 'Estado'];
      const filaCabecera = worksheet.addRow(cabecera);
      filaCabecera.eachCell((cell) => {
        cell.style = estiloCabecera;
      });
      files.forEach((file, index) => {
        const nombreLimpio = getCleanFileName(file.name);
        const tipoLabel = REGISTRO_LABELS[file.tipoRegistro] || 'SIN REGISTRO';
        const registradoA = getRegistroNombre(file) || 'SIN ASIGNAR';
        const identificador = getIdentificador(file);
        const estado = file.tipoRegistro === 'sin_registro' ? 'PENDIENTE' : 'ASIGNADO';

        const fila = [
          index + 1,
          nombreLimpio,
          tipoLabel,
          registradoA,
          identificador,
          estado
        ];

        const filaDatos = worksheet.addRow(fila);
        filaDatos.eachCell((cell, colNumber) => {
          cell.style = estiloContenidoCentrado;
          if (colNumber === 2) {
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
            cell.font = { color: { argb: '000000' } };
          }
          if (colNumber === 4) {
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
            cell.font = { color: { argb: '000000' } };
          }
          if (colNumber === 3) {
            if (file.tipoRegistro === 'participante') {
              cell.font = { color: { argb: '2E7D32' } };
            } else if (file.tipoRegistro === 'organizacion') {
              cell.font = { color: { argb: '1976D2' } };
            } else {
              cell.font = { color: { argb: 'D32F2F' } };
            }
          }
          if (colNumber === 6) {
            if (estado === 'ASIGNADO') {
              cell.font = { color: { argb: '2E7D32' } };
            } else {
              cell.font = { color: { argb: 'D32F2F' } };
            }
          }
          if (colNumber === 5 && identificador === 'N/A') {
            cell.font = { color: { argb: '757575' }, italic: true };
          }
        });
      });
      worksheet.columns = [
        { width: 8 },
        { width: 45 },
        { width: 16 },
        { width: 32 },
        { width: 20 },
        { width: 15 }
      ];
      let rowIndex = 11;
      while (rowIndex <= worksheet.rowCount) {
        const row = worksheet.getRow(rowIndex);
        const firstCellValue = row.getCell(1).value;
        if (typeof firstCellValue === 'number') {
          if (rowIndex % 2 === 0) {
            row.eachCell((cell) => {
              if (cell.col <= 6) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F8F9FA' } };
              }
            });
          }
        }
        rowIndex++;
      }
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const nombreSeguroProyecto = project.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
      const nombreSeguroMes = month.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 20);
      const nombreArchivo = `Detalles_${nombreSeguroProyecto}_${nombreSeguroMes}_${new Date().toISOString().split('T')[0]}.xlsx`;

      link.href = url;
      link.download = nombreArchivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (error) {
      console.error('Error al exportar:', error);
      alert('Error al exportar el archivo. Por favor, intente de nuevo.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      type="primary"
      icon={<DownloadOutlined />}
      onClick={exportarAExcel}
      loading={exporting}
      disabled={!files || files.length === 0}
      title="Exportar detalles a Excel"
      size="small"
    >
      Exportar Excel
    </Button>
  );
};

export default ExportarDetallesArchivos;
